/* eslint-disable no-restricted-globals */

import { getCore } from '../../../../client/core/getCore';
import { getDefaultClient } from '../../../../client/defaultClient';
import { ValueMustBeDefinedError } from '../../../../errors/ValueMustBeDefinedError';

type AddCoinbaseOnrampOrderEventListenerParams = {
  listener: (event: {
    data: Record<string, unknown>;
    eventName: string;
  }) => void;
};

/**
 * Adds a listener for Coinbase onramp order events.
 *
 * @param params.listener - The listener function to call when an event is received.
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A function to remove the listener.
 * @throws {ValueMustBeDefinedError} If the window is not available.
 * @docs https://docs.cdp.coinbase.com/onramp-&-offramp/onramp-apis/apple-pay-onramp-api#events-names
 */
export const addCoinbaseOnrampOrderEventListener = (
  { listener }: AddCoinbaseOnrampOrderEventListenerParams,
  client = getDefaultClient()
): VoidFunction => {
  const core = getCore(client);

  if (typeof window === 'undefined') {
    throw new ValueMustBeDefinedError(
      'Window is not available to listen for Coinbase onramp order events'
    );
  }

  const handleMessageEvent = (event: MessageEvent) => {
    if (event.origin !== 'https://pay.coinbase.com') {
      return;
    }

    core.logger.debug(
      '[addCoinbaseOnrampOrderEventListener] pay.coinbase.com event received',
      { event }
    );

    try {
      const coinbaseOnrampOrderEventData = JSON.parse(event.data);

      if (!coinbaseOnrampOrderEventData.eventName?.startsWith('onramp_api.')) {
        return;
      }

      listener({
        data: coinbaseOnrampOrderEventData.data,
        eventName: coinbaseOnrampOrderEventData.eventName,
      });
    } catch (error) {
      core.logger.debug(
        '[addCoinbaseOnrampOrderEventListener] Failed to parse Coinbase onramp order event data',
        {
          error,
        }
      );
    }
  };

  window.addEventListener('message', handleMessageEvent);

  return () => {
    window.removeEventListener('message', handleMessageEvent);
  };
};
